import { log, logError } from '../../utils/logging';
import {
  logDiffOperations,
  logSuggestEdit,
  logSuggestEditWarn,
  logTextBaselineComparison,
  logWordList,
  textSnapshot,
} from '../../utils/suggestEditDebug';
import { computeWordDiff as computeSharedWordDiff, DiffOp } from '../../word/utils/TextDiff';
import { TrackChangesParser } from '../../word/utils/TrackChangesParser';

/* global DOMParser, XMLSerializer, Node, Element, TreeWalker, NodeFilter */

/**
 * Represents a single character's position in the XML structure
 */
interface TextPositionMap {
  charIndex: number;           // Position in the plain text string
  run: Element;                // Parent w:r (run) element
  textNode: Node;              // The w:t text node containing this character
  offset: number;              // Character offset within the text node
  runProperties: Element | null; // w:rPr formatting properties for this run
  isFromOriginalIns: boolean;  // Whether this character comes from an original w:ins element
  originalInsElement: Element | null; // The original w:ins element if applicable
}

/**
 * Represents a diff operation (unchanged, deletion, or insertion)
 */
interface DiffOperation {
  type: 'unchanged' | 'deletion' | 'insertion';
  text: string;
  startPos: number;            // Start position in original text
  endPos: number;              // End position in original text
}

/**
 * Robust XML track changes generator
 * Uses rule-based approach to generate Word OOXML with track changes
 */
export class RobustXmlTrackChangesGenerator {
  private static readonly W_NAMESPACE = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

  /**
   * Main entry point - generates track changes XML
   * @param originalOoxml - Original Word OOXML from selection
   * @param newText - New plain text to overlay
   * @param authorName - Author name for track changes
   * @returns OOXML with w:ins and w:del elements
   */
  static generateTrackChangesXml(
    originalOoxml: string,
    newText: string,
    authorName: string = 'User',
    debugMeta?: { source?: string; clauseId?: string; clauseLabel?: string | number },
  ): string {
    try {
      log('[RobustXmlTrackChangesGenerator] Starting XML generation');
      log(`[RobustXmlTrackChangesGenerator] Original OOXML length: ${originalOoxml.length}`);
      log(`[RobustXmlTrackChangesGenerator] New text length: ${newText.length}`);

      logSuggestEdit('GENERATOR — BEFORE', {
        ...debugMeta,
        authorName,
        originalOoxmlLen: originalOoxml.length,
        newText: textSnapshot(newText),
        originalOoxmlHasRef: /REF|MERGEFORMAT|instrText/i.test(originalOoxml),
      });

      const hasFieldRefs = /REF|MERGEFORMAT|instrText/i.test(originalOoxml);
      if (hasFieldRefs) {
        log('[RobustXmlTrackChangesGenerator] REF/MERGEFORMAT/instrText detected — sanitizing OOXML (same as Editor / ParagraphXMLDisplay)');
      }

      const workingOoxml = hasFieldRefs
        ? TrackChangesParser.sanitizeFieldReferencesInOoxml(originalOoxml)
        : originalOoxml;

      logSuggestEdit('GENERATOR — after sanitize', {
        ...debugMeta,
        workingOoxmlLen: workingOoxml.length,
        sanitized: hasFieldRefs,
      });

      // Step 1–2: Build accepted text + position map in one pass (no trailing global REF strip)
      let { text: originalText, map: textMap } =
        this.buildAcceptedTextAndPositionMap(workingOoxml);

      const trimmedBaseline = this.trimLeadingTrailingWhitespaceFromTextMap(originalText, textMap);
      if (trimmedBaseline.trimmedCount > 0) {
        logSuggestEdit('GENERATOR — trimmed accepted baseline whitespace', {
          ...debugMeta,
          trimmedCount: trimmedBaseline.trimmedCount,
          before: textSnapshot(originalText),
          after: textSnapshot(trimmedBaseline.text),
        });
        originalText = trimmedBaseline.text;
        textMap = trimmedBaseline.map;
      }

      log(`[RobustXmlTrackChangesGenerator] Extracted accepted text length: ${originalText.length}`);
      log(`[RobustXmlTrackChangesGenerator] Accepted text preview: ${originalText.substring(0, 150)}`);
      log(`[RobustXmlTrackChangesGenerator] Created position map with ${textMap.length} entries`);

      logSuggestEdit('GENERATOR — accepted baseline (diff FROM this)', {
        ...debugMeta,
        originalText: textSnapshot(originalText),
        textMapEntries: textMap.length,
      });

      logTextBaselineComparison(
        'newText (engine suggestion)',
        newText,
        'originalText (OOXML accepted + position map)',
        originalText,
      );

      if (textMap.length !== originalText.length) {
        logError(
          '[RobustXmlTrackChangesGenerator] Length mismatch: extracted text length ' +
            originalText.length +
            ' vs position map ' +
            textMap.length,
        );
        const previewStart = Math.max(0, originalText.length - 20);
        logError('[RobustXmlTrackChangesGenerator] Mismatch debug', {
          textTail: originalText.slice(previewStart),
          mapTailCharIndex: textMap[textMap.length - 1]?.charIndex,
          hasFieldRefs,
          workingOoxmlLen: workingOoxml.length,
        });
        logSuggestEditWarn('GENERATOR — map length ≠ text length', {
          ...debugMeta,
          originalTextLen: originalText.length,
          mapLen: textMap.length,
        });
      }

      if (originalText.trim() === newText.trim()) {
        log('[RobustXmlTrackChangesGenerator] No changes detected, returning sanitized original XML');
        logSuggestEdit('GENERATOR — no-op (trimmed texts equal)', { ...debugMeta });
        return workingOoxml;
      }

      // Step 3: Compute hybrid word/character-level diff
      let diffOps = this.computeHybridDiff(originalText, newText, debugMeta);
      diffOps = this.extendDeletionsOverPrecedingWhitespace(originalText, diffOps);
      log(`[RobustXmlTrackChangesGenerator] Computed ${diffOps.length} diff operations`);
      logDiffOperations(originalText, diffOps);

      // Step 4: Apply diff to sanitized XML structure
      const editedXml = this.applyDiffToXml(workingOoxml, diffOps, textMap, authorName);
      log('[RobustXmlTrackChangesGenerator] Successfully generated edited XML');

      const acceptedFromResult = TrackChangesParser.extractAcceptedText(editedXml);
      logSuggestEdit('GENERATOR — AFTER applyDiffToXml', {
        ...debugMeta,
        editedXmlLen: editedXml.length,
        acceptedFromResult: textSnapshot(acceptedFromResult),
        intendedNewText: textSnapshot(newText),
        acceptedMatchesNew: acceptedFromResult.replace(/\s+/g, ' ').trim() === newText.replace(/\s+/g, ' ').trim(),
      });

      return editedXml;
    } catch (error) {
      logError('[RobustXmlTrackChangesGenerator] Failed to generate track changes XML', error);
      // Fallback: return original XML
      return originalOoxml;
    }
  }

  /**
   * Find paragraph element in XML document
   */
  private static findParagraphElement(xmlDoc: Document): Element | null {
    // Try different methods to find paragraph
    let para = xmlDoc.querySelector('w\\:p');
    if (!para) para = xmlDoc.querySelector('p');
    if (!para) {
      // Use TreeWalker to find element with local name 'p'
      const walker = xmlDoc.createTreeWalker(
        xmlDoc.documentElement,
        NodeFilter.SHOW_ELEMENT,
        {
          acceptNode: (node: Node) => {
            const elem = node as Element;
            return this.isTag(elem, 'p') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
          }
        }
      );
      para = walker.nextNode() as Element;
    }
    return para;
  }

  /**
   * Build accepted (final) plain text and a per-character XML position map in one traversal.
   * OOXML should already be sanitized (sanitizeFieldReferencesInOoxml) when it contains REF fields.
   */
  private static buildAcceptedTextAndPositionMap(ooxml: string): {
    text: string;
    map: TextPositionMap[];
  } {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(ooxml, 'text/xml');
      const paragraphElement = this.findParagraphElement(xmlDoc);
      if (!paragraphElement) {
        return { text: '', map: [] };
      }

      const positionMap: TextPositionMap[] = [];
      const textChars: string[] = [];
      this.traverseAndMapPositions(
        paragraphElement,
        positionMap,
        textChars,
        0,
        false,
        null,
      );
      return { text: textChars.join(''), map: positionMap };
    } catch (error) {
      logError('[RobustXmlTrackChangesGenerator] Error building accepted text + map', error);
      return { text: '', map: [] };
    }
  }

  private static pushMappedChar(
    positionMap: TextPositionMap[],
    textChars: string[],
    ch: string,
    meta: Omit<TextPositionMap, 'charIndex'>,
  ): void {
    const charIndex = textChars.length;
    textChars.push(ch);
    positionMap.push({ charIndex, ...meta });
  }

  /**
   * Traverse XML tree and map character positions
   * @param element - Current element
   * @param positionMap - Position map to populate (mutated)
   * @param charIndex - Current character index (mutated via return value)
   * @param isInsideOriginalIns - Whether we're currently inside an original w:ins element
   * @param originalInsElement - The original w:ins element if applicable
   * @returns Updated character index
   */
  private static traverseAndMapPositions(
    element: Element,
    positionMap: TextPositionMap[],
    textChars: string[],
    charIndex: number,
    isInsideOriginalIns: boolean,
    originalInsElement: Element | null
  ): number {
    let currentCharIndex = charIndex;

    // Check if this element is a w:ins - update context
    const localName = this.getLocalName(element);
    let currentIsInsideIns = isInsideOriginalIns;
    let currentOriginalInsElement = originalInsElement;
    
    if (localName === 'ins') {
      currentIsInsideIns = true;
      currentOriginalInsElement = element;
    }

    // Check if this is a w:r (run) element
    const isRun = this.isTag(element, 'r');
    let runElement: Element | null = null;
    let runProperties: Element | null = null;

    if (isRun) {
      runElement = element;
      // Find w:rPr (run properties) if it exists
      for (const child of Array.from(element.childNodes)) {
        if (child.nodeType === Node.ELEMENT_NODE) {
          const childElem = child as Element;
          if (this.isTag(childElem, 'rPr')) {
            runProperties = childElem;
            break;
          }
        }
      }
    }

    // Traverse child nodes
    for (const child of Array.from(element.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        // Text node - map each character
        const text = child.textContent || '';
        for (let i = 0; i < text.length; i++) {
          this.pushMappedChar(positionMap, textChars, text[i], {
            run: runElement || element,
            textNode: child,
            offset: i,
            runProperties: runProperties,
            isFromOriginalIns: currentIsInsideIns,
            originalInsElement: currentOriginalInsElement,
          });
          currentCharIndex++;
        }
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const childElement = child as Element;
        const childLocalName = this.getLocalName(childElement);

        // Skip w:del elements (we don't map deleted text, but we'll preserve them in output)
        if (childLocalName === 'del') {
          continue;
        }

        // Skip Word field instructions and field scaffolding (REF … MERGEFORMAT)
        if (
          childLocalName === 'instrtext' ||
          childLocalName === 'fldchar' ||
          childLocalName === 'fldsimple'
        ) {
          continue;
        }

        // For w:t (text) elements, extract text and map positions
        if (childLocalName === 't') {
          const text = childElement.textContent || '';
          for (let i = 0; i < text.length; i++) {
            this.pushMappedChar(positionMap, textChars, text[i], {
              run: runElement || element,
              textNode: childElement.firstChild || childElement,
              offset: i,
              runProperties: runProperties,
              isFromOriginalIns: currentIsInsideIns,
              originalInsElement: currentOriginalInsElement,
            });
            currentCharIndex++;
          }
          continue;
        }

        // Skip w:delText (deleted text)
        if (childLocalName === 'delText') {
          continue;
        }

        // For w:ins elements, traverse with updated context
        if (childLocalName === 'ins') {
          currentCharIndex = this.traverseAndMapPositions(
            childElement,
            positionMap,
            textChars,
            currentCharIndex,
            true,
            childElement,
          );
          continue;
        }

        // For w:rPr, skip (it's run properties, not text)
        if (childLocalName === 'rPr') {
          continue;
        }

        // For w:tab, map a tab character (Word renders these as whitespace)
        if (childLocalName === 'tab') {
          this.pushMappedChar(positionMap, textChars, '\t', {
            run: runElement || element,
            textNode: childElement,
            offset: 0,
            runProperties: runProperties,
            isFromOriginalIns: currentIsInsideIns,
            originalInsElement: currentOriginalInsElement,
          });
          currentCharIndex++;
          continue;
        }

        if (childLocalName === 'br') {
          this.pushMappedChar(positionMap, textChars, '\n', {
            run: runElement || element,
            textNode: childElement,
            offset: 0,
            runProperties: runProperties,
            isFromOriginalIns: currentIsInsideIns,
            originalInsElement: currentOriginalInsElement,
          });
          currentCharIndex++;
          continue;
        }

        currentCharIndex = this.traverseAndMapPositions(
          childElement,
          positionMap,
          textChars,
          currentCharIndex,
          currentIsInsideIns,
          currentOriginalInsElement,
        );
      }
    }

    return currentCharIndex;
  }

  /**
   * Trim leading/trailing whitespace from accepted text + map (Word often prefixes paragraph tabs).
   * Keeps textMap[i].charIndex aligned with text[i].
   */
  private static trimLeadingTrailingWhitespaceFromTextMap(
    text: string,
    map: TextPositionMap[],
  ): { text: string; map: TextPositionMap[]; trimmedCount: number } {
    let start = 0;
    while (start < text.length && /\s/.test(text[start])) start++;
    let end = text.length;
    while (end > start && /\s/.test(text[end - 1])) end--;
    if (start === 0 && end === text.length) {
      return { text, map, trimmedCount: 0 };
    }
    const trimmedText = text.slice(start, end);
    const trimmedMap = map.slice(start, end).map((entry, charIndex) => ({
      ...entry,
      charIndex,
    }));
    return { text: trimmedText, map: trimmedMap, trimmedCount: start + (text.length - end) };
  }

  /**
   * Word-level LCS skips whitespace between tokens; extend each deletion backward over
   * whitespace gaps so orphaned spaces (e.g. "בנספח  א'") are not left before insertions.
   */
  private static extendDeletionsOverPrecedingWhitespace(
    text: string,
    ops: DiffOperation[],
  ): DiffOperation[] {
    return ops.map((op) => {
      if (op.type !== 'deletion' || op.startPos <= 0) return op;
      let start = op.startPos;
      while (start > 0 && /\s/.test(text[start - 1])) {
        start--;
      }
      if (start === op.startPos) return op;
      return {
        ...op,
        startPos: start,
        text: text.slice(start, op.endPos),
      };
    });
  }

  /** Split text into words with start/end character indices (for hybrid diff). */
  private static splitWordsWithPositions(text: string): Array<{ word: string; startChar: number; endChar: number }> {
    const words: Array<{ word: string; startChar: number; endChar: number }> = [];
    const wordRegex = /\S+/g;
    let match;

    while ((match = wordRegex.exec(text)) !== null) {
      words.push({
        word: match[0],
        startChar: match.index,
        endChar: match.index + match[0].length
      });
    }

    return words;
  }

  /**
   * Compute word-level diff using LCS algorithm
   * @param originalText - Original plain text
   * @param newText - New plain text
   * @returns Array of diff operations with character positions
   */
  /**
   * Compute diff using the SAME algorithm as RedlineInserter (from TextDiff.ts).
   * This ensures the "Suggested edit" panel shows the exact same changes
   * that will be applied to the Word document.
   *
   * Steps:
   * 1. Normalize both texts (collapse whitespace to single spaces) — same as RedlineInserter
   * 2. Use the shared computeWordDiff from TextDiff.ts — same LCS with same tie-breaking
   * 3. Map word-level results back to character positions in the original (un-normalized) text
   */
  private static computeHybridDiff(
    originalText: string,
    newText: string,
    debugMeta?: { source?: string; clauseId?: string; clauseLabel?: string | number },
  ): DiffOperation[] {
    // 1. Normalize texts for diffing (same normalization as RedlineInserter)
    const origNormalized = originalText.replace(/\s+/g, ' ').trim();
    const newNormalized = newText.replace(/\s+/g, ' ').trim();

    logSuggestEdit('DIFF — normalized inputs', {
      ...debugMeta,
      origNormalized: textSnapshot(origNormalized),
      newNormalized: textSnapshot(newNormalized),
    });

    // 2. Use the shared word diff (same algorithm as RedlineInserter)
    const sharedDiffs: DiffOp[] = computeSharedWordDiff(origNormalized, newNormalized);
    log(`[RobustXmlTrackChangesGenerator] Shared word diff: ${sharedDiffs.length} ops`);

    const origWordsNormalized = origNormalized.split(/\s+/).filter(Boolean);
    const newWordsNormalized = newNormalized.split(/\s+/).filter(Boolean);
    logWordList('DIFF — origNormalized words', origWordsNormalized);
    logWordList('DIFF — newNormalized words', newWordsNormalized);

    logSuggestEdit('DIFF — shared word ops (LCS)', {
      ...debugMeta,
      ops: sharedDiffs.map((op, i) => ({
        i,
        type: op.type,
        words: op.words,
        wordCount: op.words.length,
      })),
    });

    // 3. Map LCS word indices to character positions in the same string used for applyDiffToXml
    const origWordPositions = this.splitWordsWithPositions(originalText);
    const origWordsRaw = origWordPositions.map((w) => w.word);

    logWordList('DIFF — originalText raw words (position map)', origWordsRaw);
    logSuggestEdit('DIFF — word index alignment check', {
      ...debugMeta,
      rawWordCount: origWordsRaw.length,
      normalizedWordCount: origWordsNormalized.length,
      countsMatch: origWordsRaw.length === origWordsNormalized.length,
      firstRawWord: origWordsRaw[0],
      firstNormWord: origWordsNormalized[0],
      lastRawWord: origWordsRaw[origWordsRaw.length - 1],
      lastNormWord: origWordsNormalized[origWordsNormalized.length - 1],
    });

    if (origWordsRaw.length !== origWordsNormalized.length) {
      logSuggestEditWarn('DIFF — word count mismatch (raw vs normalized)', {
        ...debugMeta,
        hint: 'Mapping LCS word indices to char positions will skew — likely leading/double spaces in OOXML accepted text.',
        rawWordCount: origWordsRaw.length,
        normalizedWordCount: origWordsNormalized.length,
      });
    }

    // 4. Convert word-level DiffOps to character-position DiffOperations
    const result: DiffOperation[] = [];
    let origWordIdx = 0; // Index into origWordPositions

    for (const diffOp of sharedDiffs) {
      if (diffOp.type === 'equal') {
        // Unchanged words: advance through original word positions
        const startWord = origWordIdx;
        const endWord = origWordIdx + diffOp.words.length;
        if (startWord < origWordPositions.length && endWord <= origWordPositions.length) {
          result.push({
            type: 'unchanged',
            text: diffOp.words.join(' '),
            startPos: origWordPositions[startWord].startChar,
            endPos: origWordPositions[endWord - 1].endChar
          });
        }
        origWordIdx += diffOp.words.length;
      } else if (diffOp.type === 'delete') {
        // Deleted words: map to original positions and advance
        const startWord = origWordIdx;
        const endWord = origWordIdx + diffOp.words.length;
        if (startWord < origWordPositions.length && endWord <= origWordPositions.length) {
          result.push({
            type: 'deletion',
            text: diffOp.words.join(' '),
            startPos: origWordPositions[startWord].startChar,
            endPos: origWordPositions[endWord - 1].endChar
          });
        }
        origWordIdx += diffOp.words.length;
      } else if (diffOp.type === 'insert') {
        // Inserted words: don't advance original position
        // Insert position = end of previous word, or start of text
        const insertPos = origWordIdx > 0
          ? origWordPositions[origWordIdx - 1].endChar
          : 0;
        result.push({
          type: 'insertion',
          text: diffOp.words.join(' '),
          startPos: insertPos,
          endPos: insertPos
        });
      }
    }

    log(`[RobustXmlTrackChangesGenerator] Converted to ${result.length} position-based ops`);

    logSuggestEdit('DIFF — position ops summary', {
      ...debugMeta,
      origWordIdxAfter: origWordIdx,
      origWordPositionsLen: origWordPositions.length,
      origWordIdxExhausted: origWordIdx === origWordPositions.length,
      deletionRanges: result
        .filter((o) => o.type === 'deletion')
        .map((o) => ({ start: o.startPos, end: o.endPos, text: o.text.slice(0, 60) })),
      insertionRanges: result
        .filter((o) => o.type === 'insertion')
        .map((o) => ({ at: o.startPos, text: o.text.slice(0, 60) })),
    });

    if (origWordIdx !== origWordPositions.length) {
      logSuggestEditWarn('DIFF — origWordIdx not exhausted', {
        ...debugMeta,
        origWordIdx,
        origWordPositionsLen: origWordPositions.length,
        unconsumedWords: origWordPositions.slice(origWordIdx).map((w) => w.word),
      });
    }

    return result;
  }

  /**
   * Apply diff operations to XML structure, creating new OOXML with track changes
   * @param originalOoxml - Original OOXML
   * @param diffOps - Diff operations
   * @param textMap - Text position mappings
   * @param authorName - Author name for track changes
   * @returns New OOXML with track changes
   */
  private static applyDiffToXml(
    originalOoxml: string,
    diffOps: DiffOperation[],
    textMap: TextPositionMap[],
    authorName: string
  ): string {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(originalOoxml, 'text/xml');
      
      // Find paragraph element
      const paragraphElement = this.findParagraphElement(xmlDoc);
      if (!paragraphElement) {
        logError('[RobustXmlTrackChangesGenerator] No paragraph element found for applying diff');
        return originalOoxml;
      }

      // Build new paragraph with track changes
      const newParagraph = this.buildParagraphWithTrackChanges(
        paragraphElement,
        diffOps,
        textMap,
        authorName,
        xmlDoc
      );

      // Replace old paragraph with new one
      if (paragraphElement.parentNode) {
        paragraphElement.parentNode.replaceChild(newParagraph, paragraphElement);
      } else {
        // If paragraph is root, return just the new paragraph
        return new XMLSerializer().serializeToString(newParagraph);
      }

      return new XMLSerializer().serializeToString(xmlDoc);
    } catch (error) {
      logError('[RobustXmlTrackChangesGenerator] Error applying diff to XML', error);
      return originalOoxml;
    }
  }

  /**
   * Traverse original paragraph structure and build new paragraph with track changes
   * This method preserves w:del elements in their original positions by traversing
   * the structure element-by-element rather than rebuilding from diff operations.
   */
  private static traverseAndBuildParagraph(
    originalElement: Element,
    newParent: Element,
    diffOps: DiffOperation[],
    textMap: TextPositionMap[],
    xmlDoc: Document,
    context: {
      currentTextPos: number;
      changeId: number;
      authorName: string;
      date: string;
      processedInsElements: Map<Element, Element>;
      parentInsElement: Element | null;
    }
  ): number {
    // Traverse children in order
    for (const child of Array.from(originalElement.childNodes)) {
      if (child.nodeType !== Node.ELEMENT_NODE) continue;
      
      const elem = child as Element;
      const localName = elem.localName;
      
      // Skip paragraph properties (w:pPr) - they're already copied in buildParagraphWithTrackChanges
      if (localName === 'pPr') {
        continue;
      }
      
      // Handle w:del - preserve completely in original position
      if (localName === 'del') {
        log(`[RobustXmlTrackChangesGenerator] Preserving original w:del at text pos ${context.currentTextPos}`);
        const clonedDel = elem.cloneNode(true) as Element;
        
        // If we're inside a processed w:ins, append to that, otherwise to newParent
        const targetParent = context.parentInsElement || newParent;
        targetParent.appendChild(clonedDel);
        
        // Do NOT advance text position - deleted text is not in extracted text
        continue;
      }
      
      // Handle w:ins - preserve structure, traverse children, allow nesting
      if (localName === 'ins') {
        log(`[RobustXmlTrackChangesGenerator] Processing original w:ins at text pos ${context.currentTextPos}`);
        
        // Clone w:ins element (attributes only)
        let clonedIns = context.processedInsElements.get(elem);
        if (!clonedIns) {
          clonedIns = elem.cloneNode(false) as Element;
          context.processedInsElements.set(elem, clonedIns);
          
          // Append to parent
          const targetParent = context.parentInsElement || newParent;
          targetParent.appendChild(clonedIns);
        }
        
        // Recursively traverse children with clonedIns as the parent ins element
        const oldParentIns = context.parentInsElement;
        context.parentInsElement = clonedIns;
        context.changeId = this.traverseAndBuildParagraph(
          elem, clonedIns, diffOps, textMap, xmlDoc, context
        );
        context.parentInsElement = oldParentIns;
        
        continue;
      }
      
      // Handle w:r (text runs) - apply diff operations
      if (localName === 'r') {
        const runText = this.extractTextFromRun(elem);
        const runTextLength = runText.length;
        
        if (runTextLength > 0) {
          log(`[RobustXmlTrackChangesGenerator] Processing run with ${runTextLength} chars at pos ${context.currentTextPos}`);
          
          // Apply diff operations to this text segment
          context.changeId = this.applyDiffToTextSegment(
            elem,
            context.currentTextPos,
            runTextLength,
            diffOps,
            textMap,
            newParent,
            xmlDoc,
            context
          );
          
          // Advance text position
          context.currentTextPos += runTextLength;
        } else {
          // Empty run - copy as-is
          const targetParent = context.parentInsElement || newParent;
          targetParent.appendChild(elem.cloneNode(true));
        }
        
        continue;
      }
      
      // Handle other elements (w:pPr, w:bookmarkStart, etc.) - copy as-is
      log(`[RobustXmlTrackChangesGenerator] Copying element ${localName} as-is`);
      const cloned = elem.cloneNode(true) as Element;
      const targetParent = context.parentInsElement || newParent;
      targetParent.appendChild(cloned);
    }
    
    return context.changeId;
  }
  
  /**
   * Extract text content from a run element (w:r)
   */
  private static extractTextFromRun(run: Element): string {
    let text = '';
    
    // Traverse all child nodes of the run
    for (const child of Array.from(run.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const elem = child as Element;
        if (elem.localName === 't') {
          // Found a text element - add its content
          text += elem.textContent || '';
        } else if (elem.localName === 'tab') {
          // Tab element — represent as tab character
          text += '\t';
        } else if (elem.localName === 'br') {
          // Break element — represent as newline
          text += '\n';
        } else {
          // Recursively check nested elements (shouldn't happen in normal runs, but be safe)
          const nestedText = this.extractTextFromRun(elem);
          text += nestedText;
        }
      } else if (child.nodeType === Node.TEXT_NODE) {
        // Direct text node (unlikely in OOXML, but handle it)
        text += child.textContent || '';
      }
    }
    
    return text;
  }
  
  /**
   * Apply diff operations to a text segment (from a run)
   * Returns updated changeId
   */
  private static applyDiffToTextSegment(
    originalRun: Element,
    segmentStartPos: number,
    segmentLength: number,
    diffOps: DiffOperation[],
    textMap: TextPositionMap[],
    newParent: Element,
    xmlDoc: Document,
    context: {
      currentTextPos: number;
      changeId: number;
      authorName: string;
      date: string;
      processedInsElements: Map<Element, Element>;
      parentInsElement: Element | null;
    }
  ): number {
    const segmentEndPos = segmentStartPos + segmentLength;
    
    // Find diff operations that overlap with this text segment
    const relevantOps = diffOps.filter(op => {
      if (op.type === 'insertion') {
        return op.startPos >= segmentStartPos && op.startPos < segmentEndPos;
      } else {
        return (op.startPos < segmentEndPos && op.endPos > segmentStartPos);
      }
    });
    
    if (relevantOps.length === 0) {
      // No changes in this segment - copy run as-is
      log(`[RobustXmlTrackChangesGenerator] No changes for segment ${segmentStartPos}-${segmentEndPos}, copying run`);
      const targetParent = context.parentInsElement || newParent;
      targetParent.appendChild(originalRun.cloneNode(true));
      return context.changeId;
    }
    
    log(`[RobustXmlTrackChangesGenerator] Found ${relevantOps.length} ops for segment ${segmentStartPos}-${segmentEndPos}`);
    
    // Process the segment character by character, applying diff operations
    let charPos = segmentStartPos;
    
    while (charPos < segmentEndPos) {
      // Find the operation at this position
      let currentOp: DiffOperation | null = null;
      
      // Check for insertions at this position first
      const insertionOp = relevantOps.find(
        op => op.type === 'insertion' && op.startPos === charPos
      );
      
      if (insertionOp) {
        currentOp = insertionOp;
      } else {
        // Check for deletion or unchanged
        currentOp = relevantOps.find(
          op => op.type !== 'insertion' && charPos >= op.startPos && charPos < op.endPos
        ) || null;
      }
      
      if (!currentOp) {
        // No operation - treat as unchanged. Always append to paragraph (newParent)
        // so unchanged text is not shown as insertion in the Suggested edit panel.
        const char = charPos < textMap.length ? textMap[charPos] : null;
        if (char) {
          // Copy this character with original formatting
          const targetParent = newParent;
          const lastChild = targetParent.lastChild;
          const isLastChildRun = lastChild && lastChild.nodeType === Node.ELEMENT_NODE && 
                                 this.isTag(lastChild as Element, 'r');
          
          if (!isLastChildRun || (charPos > segmentStartPos && textMap[charPos - 1]?.run !== char.run)) {
            const newRun = this.preserveFormatting(char.run, '');
            targetParent.appendChild(newRun);
          }
          
          const lastRun = targetParent.lastChild as Element;
          const textElem = this.getOrCreateTextElement(lastRun, xmlDoc);
          textElem.textContent = (textElem.textContent || '') + this.getCharFromMap(char);
        }
        charPos++;
        continue;
      }
      
      if (currentOp.type === 'insertion') {
        // Create insertion
        log(`[RobustXmlTrackChangesGenerator] Creating insertion at pos ${charPos}`);
        
        // Use parentInsElement as target when inside a <w:ins>, otherwise newParent.
        // We always use createInsertion and pass the correct parent directly,
        // avoiding the fragile processedInsElements lookup in createNestedInsertion.
        const insTarget = context.parentInsElement || newParent;
        const parentInsAuthor =
          context.parentInsElement?.getAttribute('w:author') ||
          context.parentInsElement?.getAttributeNS(
            'http://schemas.openxmlformats.org/wordprocessingml/2006/main',
            'author',
          ) ||
          '';
        logSuggestEdit('[TrackChangeAuthor] createInsertion placement', {
          charPos,
          newAuthor: context.authorName,
          appendTo: context.parentInsElement ? 'nested_inside_parent_w_ins' : 'paragraph_level',
          parentInsAuthor: parentInsAuthor || null,
          nestedInsideCounterpartyIns:
            Boolean(context.parentInsElement) && parentInsAuthor !== context.authorName,
          insertTextLen: currentOp.text.length,
          insertSnippet: currentOp.text.slice(0, 80),
        });
        context.changeId = this.createInsertion(
          currentOp, textMap, insTarget, xmlDoc, context.authorName, context.date, context.changeId
        );
        
        // Don't advance charPos - insertions don't consume original text
        // Remove this operation from relevantOps
        const opIndex = relevantOps.indexOf(currentOp);
        if (opIndex > -1) relevantOps.splice(opIndex, 1);
        continue;
      }
      
      if (currentOp.type === 'deletion') {
        // Process the deleted portion within this segment
        const delStart = Math.max(currentOp.startPos, segmentStartPos);
        const delEnd = Math.min(currentOp.endPos, segmentEndPos);
        
        log(`[RobustXmlTrackChangesGenerator] Creating deletion for pos ${delStart}-${delEnd}`);
        
        // Create a deletion operation for this segment
        const segmentDelOp: DiffOperation = {
          type: 'deletion',
          text: currentOp.text.substring(delStart - currentOp.startPos, delEnd - currentOp.startPos),
          startPos: delStart,
          endPos: delEnd
        };
        
        // Use parentInsElement as target when inside a <w:ins>, otherwise newParent.
        // NOTE: We always use createDeletion (not createNestedDeletion) because
        // the textMap's originalInsElement references come from a different DOM parse
        // than the elements in processedInsElements, so the lookup always fails.
        // context.parentInsElement is already the correct cloned <w:ins> element.
        const targetParent = context.parentInsElement || newParent;
        context.changeId = this.createDeletion(
          segmentDelOp, textMap, targetParent, xmlDoc, context.authorName, context.date, context.changeId
        );
        
        // Advance to end of deleted portion
        charPos = delEnd;
        continue;
      }
      
      if (currentOp.type === 'unchanged') {
        // Process unchanged portion within this segment. Always append to paragraph (newParent)
        // so unchanged text is not shown as insertion in the Suggested edit panel.
        const unchangedStart = Math.max(currentOp.startPos, segmentStartPos);
        const unchangedEnd = Math.min(currentOp.endPos, segmentEndPos);
        
        for (let i = unchangedStart; i < unchangedEnd; i++) {
          const char = i < textMap.length ? textMap[i] : null;
          if (char) {
            const targetParent = newParent;
            const lastChild = targetParent.lastChild;
            const isLastChildRun = lastChild && lastChild.nodeType === Node.ELEMENT_NODE && 
                                   this.isTag(lastChild as Element, 'r');
            
            if (!isLastChildRun || (i > segmentStartPos && textMap[i - 1]?.run !== char.run)) {
              const newRun = this.preserveFormatting(char.run, '');
              targetParent.appendChild(newRun);
            }
            
            const lastRun = targetParent.lastChild as Element;
            const textElem = this.getOrCreateTextElement(lastRun, xmlDoc);
            textElem.textContent = (textElem.textContent || '') + this.getCharFromMap(char);
          }
        }
        
        charPos = unchangedEnd;
        continue;
      }
    }

    return context.changeId;
  }

  /**
   * Build new paragraph element with track changes
   * Preserves original w:ins and w:del elements using structural traversal
   */
  private static buildParagraphWithTrackChanges(
    originalParagraph: Element,
    diffOps: DiffOperation[],
    textMap: TextPositionMap[],
    authorName: string,
    xmlDoc: Document
  ): Element {
    log('[RobustXmlTrackChangesGenerator] Building paragraph with structural traversal');
    log(`[RobustXmlTrackChangesGenerator] Original paragraph has ${originalParagraph.childNodes.length} children`);
    log(`[RobustXmlTrackChangesGenerator] Diff operations: ${diffOps.length}`);
    log(`[RobustXmlTrackChangesGenerator] Text map entries: ${textMap.length}`);
    
    // Create new paragraph element with same attributes
    const newPara = xmlDoc.createElementNS(this.W_NAMESPACE, 'w:p');
    
    // Copy attributes from original paragraph
    for (let i = 0; i < originalParagraph.attributes.length; i++) {
      const attr = originalParagraph.attributes[i];
      newPara.setAttribute(attr.name, attr.value);
    }

    // Copy paragraph properties (w:pPr) if they exist - do this before traversal
    // so they appear first in the new paragraph
    for (const child of Array.from(originalParagraph.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const elem = child as Element;
        if (this.isTag(elem, 'pPr')) {
          newPara.appendChild(elem.cloneNode(true));
          break;
        }
      }
    }

    // Initialize context for traversal
    const date = new Date().toISOString();
    const context = {
      currentTextPos: 0,
      changeId: Math.floor(Math.random() * 1000000),
      authorName,
      date,
      processedInsElements: new Map<Element, Element>(),
      parentInsElement: null as Element | null
    };

    // Traverse the original paragraph structure
    this.traverseAndBuildParagraph(
      originalParagraph,
      newPara,
      diffOps,
      textMap,
      xmlDoc,
      context
    );

    // Emit any insertions at or beyond the end of the text
    // (startPos === textMap.length, i.e. inserted after the last character)
    const endInsertions = diffOps.filter(
      op => op.type === 'insertion' && op.startPos >= context.currentTextPos
    );
    for (const ins of endInsertions) {
      context.changeId = this.createInsertion(
        ins, textMap, newPara, xmlDoc, context.authorName, context.date, context.changeId
      );
    }

    log(`[RobustXmlTrackChangesGenerator] Finished building paragraph:`, {
      finalTextPos: context.currentTextPos,
      finalChangeId: context.changeId,
      newParaChildren: newPara.childNodes.length,
      processedInsElements: context.processedInsElements.size
    });
    
    // Serialize to verify we have content
    const serializer = new XMLSerializer();
    const serialized = serializer.serializeToString(newPara);
    log(`[RobustXmlTrackChangesGenerator] Serialized new paragraph length: ${serialized.length}`);
    log(`[RobustXmlTrackChangesGenerator] Serialized preview: ${serialized.substring(0, 300)}`);
    
    return newPara;
  }


  /**
   * Create a deletion element (not inside original w:ins)
   */
  private static createDeletion(
    op: DiffOperation,
    textMap: TextPositionMap[],
    newPara: Element,
    xmlDoc: Document,
    authorName: string,
    date: string,
    changeId: number
  ): number {
    const delElem = xmlDoc.createElementNS(this.W_NAMESPACE, 'w:del');
    delElem.setAttribute('w:id', String(changeId));
    delElem.setAttribute('w:author', authorName);
    delElem.setAttribute('w:date', date);

    // Copy original runs for deleted text — use textMap for the actual characters
    // (op.text may have single-space joins while positions span real whitespace like tabs)
    for (let i = op.startPos; i < op.endPos; i++) {
      if (i >= textMap.length) break;
      
      const posMap = textMap[i];
      if (i === op.startPos || (i > op.startPos && textMap[i - 1].run !== posMap.run)) {
        const newRun = this.preserveFormatting(posMap.run, '');
        delElem.appendChild(newRun);
      }
      
      // Get the actual character from textMap instead of op.text to avoid length mismatches
      const lastRun = delElem.lastChild as Element;
      const textElem = this.getOrCreateTextElement(lastRun, xmlDoc);
      textElem.textContent = (textElem.textContent || '') + this.getCharFromMap(posMap);
    }
    
    newPara.appendChild(delElem);
    return changeId + 1;
  }

  /**
   * Add spaces before and after insertion text to prevent words from sticking together
   * @param insertionText - Text being inserted
   * @returns Text with spaces added before and after
   */
  private static addSpacingToInsertion(insertionText: string): string {
    let result = insertionText.trim();
    
    // Add space before if not already present
    if (result.length > 0 && !result.startsWith(' ')) {
      result = ' ' + result;
    }
    
    // Add space after if not already present
    if (result.length > 0 && !result.endsWith(' ')) {
      result = result + ' ';
    }
    
    return result;
  }

  /**
   * Create an insertion element (not inside original w:ins)
   */
  private static createInsertion(
    op: DiffOperation,
    textMap: TextPositionMap[],
    newPara: Element,
    xmlDoc: Document,
    authorName: string,
    date: string,
    changeId: number
  ): number {
    const insElem = xmlDoc.createElementNS(this.W_NAMESPACE, 'w:ins');
    insElem.setAttribute('w:id', String(changeId));
    insElem.setAttribute('w:author', authorName);
    insElem.setAttribute('w:date', date);
    logSuggestEdit('[TrackChangeAuthor] w:ins element created', {
      changeId,
      authorName,
      parentTag: newPara.localName || newPara.tagName,
      parentAuthor:
        newPara.getAttribute('w:author') ||
        newPara.getAttributeNS('http://schemas.openxmlformats.org/wordprocessingml/2006/main', 'author') ||
        null,
    });

    // Determine formatting for insertion (inherit from adjacent runs)
    let referenceRun: Element | null = null;
    if (op.startPos > 0 && op.startPos <= textMap.length) {
      referenceRun = textMap[Math.min(op.startPos, textMap.length - 1)].run;
    } else if (textMap.length > 0) {
      referenceRun = textMap[0].run;
    }

    // Add spaces before and after insertion to prevent words from sticking together
    const spacedText = this.addSpacingToInsertion(op.text);

    // Create run for inserted text
    const newRun = referenceRun ? this.preserveFormatting(referenceRun, spacedText) : this.createBasicRun(xmlDoc, spacedText);
    insElem.appendChild(newRun);
    
    newPara.appendChild(insElem);
    return changeId + 1;
  }

  /**
   * Get or create w:t element in a run
   */
  /**
   * Get the actual character from a textMap entry, handling <w:tab/> and <w:br/> elements.
   */
  private static getCharFromMap(posMap: TextPositionMap): string {
    if (posMap.textNode.nodeType === Node.ELEMENT_NODE) {
      const localName = this.getLocalName(posMap.textNode as Element);
      if (localName === 'tab') return '\t';
      if (localName === 'br') return '\n';
    }
    return posMap.textNode.textContent?.[posMap.offset] || '';
  }

  private static getOrCreateTextElement(run: Element, xmlDoc: Document): Element {
    // Look for existing w:t element
    for (const child of Array.from(run.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const elem = child as Element;
        if (this.isTag(elem, 't')) {
          return elem;
        }
      }
    }
    
    // Create new w:t element
    const textElem = xmlDoc.createElementNS(this.W_NAMESPACE, 'w:t');
    textElem.setAttribute('xml:space', 'preserve');
    run.appendChild(textElem);
    return textElem;
  }

  /**
   * Create basic run with text (no special formatting)
   */
  private static createBasicRun(xmlDoc: Document, text: string): Element {
    const run = xmlDoc.createElementNS(this.W_NAMESPACE, 'w:r');
    const textElem = xmlDoc.createElementNS(this.W_NAMESPACE, 'w:t');
    textElem.setAttribute('xml:space', 'preserve');
    textElem.textContent = text;
    run.appendChild(textElem);
    return run;
  }

  /**
   * Preserve formatting from original run to new run
   * @param originalRun - Original w:r element
   * @param newText - New text for the run
   * @returns New w:r element with copied formatting
   */
  private static preserveFormatting(originalRun: Element, newText: string): Element {
    // Create new run in the same document
    const xmlDoc = originalRun.ownerDocument;
    if (!xmlDoc) {
      logError('[RobustXmlTrackChangesGenerator] No owner document for original run');
      return originalRun.cloneNode(false) as Element;
    }

    const newRun = xmlDoc.createElementNS(this.W_NAMESPACE, 'w:r');
    
    // Copy attributes from original run
    for (let i = 0; i < originalRun.attributes.length; i++) {
      const attr = originalRun.attributes[i];
      newRun.setAttribute(attr.name, attr.value);
    }

    // Find and copy w:rPr (run properties) from original run
    let runProperties: Element | null = null;
    for (const child of Array.from(originalRun.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const elem = child as Element;
        if (this.isTag(elem, 'rPr')) {
          runProperties = elem.cloneNode(true) as Element;
          break;
        }
      }
    }

    // Add run properties to new run if they exist
    if (runProperties) {
      newRun.appendChild(runProperties);
    }

    // Add text element if text is provided
    if (newText) {
      const textElem = xmlDoc.createElementNS(this.W_NAMESPACE, 'w:t');
      textElem.setAttribute('xml:space', 'preserve');
      textElem.textContent = newText;
      newRun.appendChild(textElem);
    }

    return newRun;
  }

  /**
   * Helper: Get local name of element (handles namespaces)
   */
  private static getLocalName(element: Element): string {
    return element.localName || element.tagName.split(':').pop() || element.tagName;
  }

  /**
   * Helper: Check if element matches a tag name
   */
  private static isTag(element: Element, tagName: string): boolean {
    const localName = this.getLocalName(element);
    return localName === tagName || element.tagName === tagName || element.tagName.endsWith(`:${tagName}`);
  }
}

